/**
 * Service API pour OCR Manuscrit V2
 * Endpoints pour générer et traiter les relevés avec le nouveau système manuscrit
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface GenererReleveManuscritRequest {
  matiere_id: number;
  matiere_ids?: number[];
  niveau: string;
  option: string;
  semestre: string;
  annee_academique: string;
  type_examen: 'normal' | 'rattrapage';
  enseignant_nom?: string;
  enseignant_id?: number;
  format: 'cases_separees' | 'zone_guidee' | 'simple';
  poles?: string[];
}

export interface GenererReleveManuscritResponse {
  success: boolean;
  releve_id: number;
  code_releve: string;
  fichier_pdf: string;
  nombre_copies: number;
  format: string;
}

export interface LancerOCRManuscritV2Response {
  success: boolean;
  message: string;
  task_id?: string;
  releve_id: number;
  methode: 'asynchrone' | 'synchrone';
  result?: {
    total_notes: number;
    notes_haute_confiance: number;
    notes_a_verifier: number;
    duree_secondes: number;
  };
}

export interface LigneOCRManuscritV2 {
  id: number;
  numero_ligne: number;
  code_copie_detecte: string;
  note_manuscrite_detectee: number | null;
  confiance_manuscrit: number;
  note_finale: number | null;
  source_note: 'cases' | 'manuscrit' | 'croise' | 'manuel' | 'incertain';
  necessite_verification: boolean;
  commentaire: string;
  image_base64?: string;
  etudiant_nom?: string;
  etudiant_matricule?: string;
  etudiant_classe?: string;
  copie_examen?: {
    id: number;
    code_copie: string;
    etudiant?: {
      nom: string;
      prenoms: string;
      matricule: string;
      classe?: string;
    };
  };
  copie_examen_detail?: {
    id: number;
    code_copie: string;
    etudiant?: {
      nom: string;
      prenoms: string;
      matricule: string;
      classe?: string;
    };
  };
}

export interface OptionsAvecCopiesMatiere {
  key: string;
  matiere_ids: number[];
  nom_matiere: string;
  niveau: string;
  semestre: number;
  nb_copies: number;
  options?: string[];
  enseignants?: Array<{ id: number; nom: string }>;
  classes?: string[];
  poles?: string[];
  associes_par?: string[];
}

export interface OptionsAvecCopiesResponse {
  niveaux: string[];
  options: string[];
  matieres: OptionsAvecCopiesMatiere[];
}

export const ocrApiV2 = createApi({
  reducerPath: 'ocrApiV2',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['ReleveCorrectionV2', 'LignesOCRV2'],
  endpoints: (builder) => ({
    // Options/niveaux/matières ayant des copies associées
    getOptionsAvecCopies: builder.query<OptionsAvecCopiesResponse, void>({
      query: () => '/ocr/releves-correction-v2/options_avec_copies/',
      providesTags: ['ReleveCorrectionV2'],
    }),

    // Générer un relevé manuscrit
    genererReleveManuscrit: builder.mutation<GenererReleveManuscritResponse, GenererReleveManuscritRequest>({
      query: (data) => ({
        url: '/ocr/releves-correction-v2/generer_manuscrit/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ReleveCorrectionV2'],
    }),

    // Lancer l'OCR manuscrit V2
    lancerOCRManuscritV2: builder.mutation<LancerOCRManuscritV2Response, number>({
      query: (releveId) => ({
        url: `/ocr/releves-correction-v2/${releveId}/lancer_ocr_manuscrit_v2/`,
        method: 'POST',
      }),
      invalidatesTags: ['ReleveCorrectionV2', 'LignesOCRV2'],
    }),

    // Récupérer les lignes OCR d'un relevé (mode light par défaut : sans images)
    getLignesOCRManuscritV2: builder.query<LigneOCRManuscritV2[], { releveId: number; aVerifier?: boolean; withImages?: boolean }>({
      query: ({ releveId, aVerifier, withImages }) => {
        const params: Record<string, string> = {};
        if (aVerifier) params.a_verifier = 'true';
        if (withImages) params.with_images = 'true';
        return {
          url: `/ocr/releves-correction-v2/${releveId}/lignes_ocr/`,
          params,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'LignesOCRV2' as const, id })),
              { type: 'LignesOCRV2', id: 'LIST' },
            ]
          : [{ type: 'LignesOCRV2', id: 'LIST' }],
    }),

    // Charger l'image d'une ligne précise (chargement à la demande pour éviter
    // les payloads énormes quand on liste toutes les lignes d'un relevé)
    getImageLigneOCRV2: builder.query<{ ligne_id: number; image_base64: string }, { releveId: number; ligneId: number }>({
      query: ({ releveId, ligneId }) => ({
        url: `/ocr/releves-correction-v2/${releveId}/image_ligne/${ligneId}/`,
      }),
      keepUnusedDataFor: 600,
    }),

    // Codes copies de la matière absents des lignes OCR (pour identifier les manqués)
    getCodesManquantsOCRV2: builder.query<
      {
        total_attendu: number;
        total_present: number;
        nb_manquants: number;
        codes_manquants: Array<{ code: string; matricule: string | null; nom: string | null; classe: string | null }>;
      },
      number
    >({
      query: (releveId) => `/ocr/releves-correction-v2/${releveId}/codes_manquants/`,
      providesTags: (result, error, releveId) => [{ type: 'LignesOCRV2', id: `manquants-${releveId}` }],
    }),

    // Valider une ligne OCR
    // Pas d'invalidatesTags : l'optimistic update côté client met à jour le cache
    // instantanément. Le refetch bloque inutilement l'UI quand le serveur est lent.
    validerLigneOCRManuscritV2: builder.mutation<
      { success: boolean; message: string },
      { releveId: number; ligneId: number; note_finale?: number; commentaire?: string }
    >({
      query: ({ releveId, ligneId, note_finale, commentaire }) => ({
        url: `/ocr/releves-correction-v2/${releveId}/valider_ligne/`,
        method: 'POST',
        body: {
          ligne_id: ligneId,
          note_finale,
          commentaire,
        },
      }),
    }),

    // Valider tout le relevé
    validerToutReleveManuscritV2: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (releveId) => ({
        url: `/ocr/releves-correction-v2/${releveId}/valider_tout/`,
        method: 'POST',
      }),
      invalidatesTags: ['ReleveCorrectionV2', 'LignesOCRV2'],
    }),

    // Renvoyer les notes du relevé OCR vers les NoteExamen
    // (utile pour rétablir les notes manquantes ou écraser une saisie)
    renvoyerNotesReleveV2: builder.mutation<
      {
        success: boolean; message: string;
        nb_crees: number; nb_modifies: number; nb_ignores: number;
        nb_skip_pas_copie: number; nb_total_traites: number;
        recalcul_effectue: number;
        details_par_matiere: Record<string, number>;
        ecraser: boolean;
      },
      { releveId: number; ecraser: boolean }
    >({
      query: ({ releveId, ecraser }) => ({
        url: `/ocr/releves-correction-v2/${releveId}/renvoyer-notes/`,
        method: 'POST',
        body: { ecraser },
      }),
      invalidatesTags: ['ReleveCorrectionV2', 'LignesOCRV2'],
    }),

    ajouterCopieReleve: builder.mutation<
      {
        success?: boolean;
        message?: string;
        ligne_id?: number;
        etudiant?: { nom: string; matricule: string; classe: string };
        reassociation?: boolean;
        conflit?: {
          type: string;
          copie_code: string;
          matiere_actuelle: { id: number; nom: string; code: string };
          matiere_releve: { id: number; nom: string; code: string };
          etudiant: { nom: string; matricule: string; classe: string };
          enseignant_actuel: string | null;
        };
        error?: string;
      },
      { releveId: number; code_copie: string; force?: boolean; note_finale?: number | null; absent?: boolean }
    >({
      query: ({ releveId, ...data }) => ({
        url: `/ocr/releves-correction-v2/${releveId}/ajouter_copie/`,
        method: 'POST',
        body: data,
      }),
      // Pas d'invalidatesTags : l'optimistic update maintient le cache à jour
      // et évite un refetch coûteux quand le serveur est chargé.
    }),

    // Supprimer une ligne OCR (code en trop, doublon, etc.)
    // Pas d'invalidatesTags : l'optimistic update côté client maintient le cache à jour,
    // et on évite un refetch coûteux quand le serveur est chargé.
    supprimerLigneOCRV2: builder.mutation<
      { success: boolean; message: string; nombre_copies_restant: number },
      { releveId: number; ligneId: number }
    >({
      query: ({ releveId, ligneId }) => ({
        url: `/ocr/releves-correction-v2/${releveId}/supprimer_ligne/`,
        method: 'POST',
        body: { ligne_id: ligneId },
      }),
    }),

    // Modifier le code d'une ligne OCR (correction d'un caractère mal lu)
    modifierCodeLigneOCRV2: builder.mutation<
      { success: boolean; message: string; matched: boolean; reassociation_conflit: any | null },
      { releveId: number; ligneId: number; nouveauCode: string }
    >({
      query: ({ releveId, ligneId, nouveauCode }) => ({
        url: `/ocr/releves-correction-v2/${releveId}/modifier_code_ligne/`,
        method: 'POST',
        body: { ligne_id: ligneId, nouveau_code: nouveauCode },
      }),
      invalidatesTags: ['ReleveCorrectionV2', 'LignesOCRV2'],
    }),
  }),
});

export const {
  useGetOptionsAvecCopiesQuery,
  useGenererReleveManuscritMutation,
  useLancerOCRManuscritV2Mutation,
  useGetLignesOCRManuscritV2Query,
  useGetImageLigneOCRV2Query,
  useGetCodesManquantsOCRV2Query,
  useLazyGetCodesManquantsOCRV2Query,
  useValiderLigneOCRManuscritV2Mutation,
  useValiderToutReleveManuscritV2Mutation,
  useRenvoyerNotesReleveV2Mutation,
  useAjouterCopieReleveMutation,
  useSupprimerLigneOCRV2Mutation,
  useModifierCodeLigneOCRV2Mutation,
} = ocrApiV2;
